import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { formatApiError } from "@/lib/api";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.5 29.3 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.5 29.3 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 43.5c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39 16.2 43.5 24 43.5z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.2-.1-2.3-.4-3.5z" />
  </svg>
);

const AuthModal = () => {
  const { authOpen, setAuthOpen, login, register, googleLogin } = useAuth();
  const { t } = useLang();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success(`${t("auth.welcome")} ${form.name || form.email.split("@")[0]}!`);
      setAuthOpen(false);
      setForm({ name: "", email: "", password: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-border focus:border-gold text-foreground px-1 py-3 placeholder:text-muted-foreground transition-colors outline-none font-sans";

  return (
    <AnimatePresence>
      {authOpen && (
        <motion.div
          data-testid="auth-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setAuthOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-card border border-gold/20 rounded-sm p-8 md:p-10"
          >
            <button
              data-testid="auth-close"
              onClick={() => setAuthOpen(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-gold transition-colors"
            >
              <X size={22} />
            </button>

            <h3 className="font-serif text-3xl text-foreground">
              {mode === "login" ? t("auth.loginTitle") : t("auth.registerTitle")}
            </h3>

            <button
              data-testid="google-login"
              onClick={googleLogin}
              className="mt-6 w-full flex items-center justify-center gap-3 border border-border hover:border-gold/50 rounded-sm py-3 text-foreground font-sans text-sm transition-colors"
            >
              <GoogleIcon />
              {t("auth.google")}
            </button>

            <div className="flex items-center gap-3 my-5">
              <span className="h-px flex-1 bg-border" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{t("auth.or")}</span>
              <span className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={submit} className="space-y-5">
              {mode === "register" && (
                <input
                  data-testid="auth-name"
                  name="name"
                  required
                  value={form.name}
                  onChange={onChange}
                  placeholder={t("auth.name")}
                  className={inputCls}
                />
              )}
              <input
                data-testid="auth-email"
                name="email"
                type="email"
                required
                value={form.email}
                onChange={onChange}
                placeholder={t("auth.email")}
                className={inputCls}
              />
              <input
                data-testid="auth-password"
                name="password"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={onChange}
                placeholder={t("auth.password")}
                className={inputCls}
              />
              <button
                data-testid="auth-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-gold text-black font-sans font-semibold text-sm uppercase tracking-[0.15em] py-3.5 rounded-sm hover:bg-gold-deep transition-colors disabled:opacity-60"
              >
                {mode === "login" ? t("auth.doLogin") : t("auth.doRegister")}
              </button>
            </form>

            <button
              data-testid="auth-switch"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="mt-5 w-full text-center text-sm text-muted-foreground hover:text-gold transition-colors font-sans"
            >
              {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;
